import { useEffect } from "react";

const SITE = "https://nickrobotic.com";

export interface SeoAlternate {
  hreflang: string;
  href: string;
}

export interface SeoProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: "website" | "article" | "product";
  noindex?: boolean;
  jsonLd?: object | object[];
  /** Explicit hreflang alternates. When provided, overrides the default PT/EN/x-default fallback. */
  alternates?: SeoAlternate[];
  /**
   * Document language for this route. Sets <html lang> so it matches the
   * page's hreflang self-link — Seobility flagged "Different language for
   * link pointing to page itself" on EN landings because index.html ships
   * with <html lang="pt-br"> and never changed on /en/* routes.
   * Inferred from `alternates` when omitted.
   */
  locale?: "pt-BR" | "en";
}

/**
 * Per-route SEO head — pure DOM, no react-helmet-async.
 *
 * Removing helmet shaves ~12KB gzip from the initial bundle and removes
 * <HelmetProvider> from the React tree, which used to add an extra context
 * layer on every render. Effects below mutate document.head directly and
 * clean up tags they own on unmount so route changes do not leak meta.
 *
 * Limitation: tags only appear after JS hydrates. Social preview crawlers
 * that don't run JS still see the sitewide og:* baked into index.html, which
 * is the right fallback. JS-executing crawlers (Googlebot) see the per-route
 * tags. This matches helmet's behaviour exactly.
 */
const OWNED_ATTR = "data-seo";

function upsertMeta(key: "name" | "property", keyValue: string, content: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${keyValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, keyValue);
    el.setAttribute(OWNED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertCanonical(href: string): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    el.setAttribute(OWNED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
}

function upsertHreflang(hreflang: string, href: string): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = "alternate";
    el.setAttribute("hreflang", hreflang);
    el.setAttribute(OWNED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
}

export default function Seo({
  title,
  description,
  path = "/",
  image = `${SITE}/og-image.jpg`,
  type = "website",
  noindex,
  jsonLd,
  alternates,
  locale,
}: SeoProps) {
  useEffect(() => {
    const url = `${SITE}${path}`;
    document.title = title;

    // Sync <html lang> with the page locale so hreflang self-link matches the
    // document language. Infer from explicit alternates when not passed.
    const inferredLocale: "pt-BR" | "en" =
      locale ??
      (alternates?.find((a) => a.href === url)?.hreflang.toLowerCase().startsWith("en") ? "en" : "pt-BR");
    const previousLang = document.documentElement.lang;
    document.documentElement.lang = inferredLocale === "en" ? "en" : "pt-BR";

    upsertMeta("name", "description", description);
    upsertCanonical(url);

    // Hreflang: only emit alternates we explicitly know exist as real URLs.
    // Default = pt-BR + x-default pointing to self (the canonical PT URL).
    // Pages with real EN twin URLs must pass `alternates` explicitly.
    // Stale alternates from previous routes are pruned below.
    document.head.querySelectorAll(`link[rel="alternate"][hreflang][${OWNED_ATTR}="1"]`).forEach((n) => n.remove());
    if (alternates && alternates.length > 0) {
      alternates.forEach((a) => upsertHreflang(a.hreflang, a.href));
      if (!alternates.some((a) => a.hreflang === "x-default")) {
        upsertHreflang("x-default", url);
      }
    } else {
      upsertHreflang("pt-BR", url);
      upsertHreflang("x-default", url);
    }

    upsertMeta("name", "robots", noindex
      ? "noindex, follow, max-image-preview:large"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:site_name", "NickRobotic");
    // og:locale tracks the document language; emit alternate when the other
    // locale has a real URL declared via `alternates`.
    upsertMeta("property", "og:locale", inferredLocale === "en" ? "en_US" : "pt_BR");
    const otherLocaleTag = inferredLocale === "en" ? "pt_BR" : "en_US";
    const otherLocalePrefix = inferredLocale === "en" ? "pt" : "en";
    document.head.querySelectorAll(`meta[property="og:locale:alternate"][${OWNED_ATTR}="1"]`).forEach((n) => n.remove());
    if (alternates && alternates.some((a) => a.hreflang.toLowerCase().startsWith(otherLocalePrefix))) {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:locale:alternate");
      m.setAttribute("content", otherLocaleTag);
      m.setAttribute(OWNED_ATTR, "1");
      document.head.appendChild(m);
    }

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // JSON-LD: append fresh nodes per render, remove on cleanup.
    // Defensive dedupe (GSC reported "FAQPage field duplicated" on /produtos
    // and /painel-smm): when the consumer-supplied array contains two
    // entries with the same @type, keep only the last (avoids two FAQPage
    // blocks reaching the head); and before appending, sweep ALL owned
    // ld scripts already in the head — not just the closure's previous
    // batch — to defend against any cleanup that may have leaked across
    // re-renders.
    const rawLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    const seenTypes = new Set<string>();
    const ldArray: object[] = [];
    for (let i = rawLdArray.length - 1; i >= 0; i--) {
      const item = rawLdArray[i] as any;
      const t = item && typeof item === "object" ? String(item["@type"] ?? "__") : "__";
      if (seenTypes.has(t)) continue;
      seenTypes.add(t);
      ldArray.unshift(item);
    }
    document.head
      .querySelectorAll(`script[type="application/ld+json"][${OWNED_ATTR}="ld"]`)
      .forEach((n) => n.remove());
    const ldNodes: HTMLScriptElement[] = ldArray.map((ld) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(OWNED_ATTR, "ld");
      s.text = JSON.stringify(ld);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      ldNodes.forEach((n) => n.remove());
      // Restore previous <html lang> so unmount doesn't leak this route's locale.
      document.documentElement.lang = previousLang || "pt-BR";
    };
  }, [title, description, path, image, type, noindex, locale, JSON.stringify(jsonLd), JSON.stringify(alternates)]);

  return null;
}
